import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, filter, firstValueFrom, takeUntil } from 'rxjs';

import { ProjectContextService } from '../../core/services/project-context.service';
import { AlertsService } from '../../core/services/alerts.service';
import { AuthService } from '../../core/services/auth.service';
import { ProjectsStoreService } from '../../core/services/projects-store.service';
import { AlertDto } from '../../core/models/alerts.models';

type ProjectOption = {
  id: string;
  name: string;
  clientId?: string;
};

type ClientOption = {
  id: string;
  name: string;
  projects: ProjectOption[];
};

@Component({
  selector: 'app-topbar',
  standalone: false,
  templateUrl: './topbar.html',
  styleUrl: './topbar.scss',
})
export class Topbar implements OnInit, OnDestroy {
  protected projectMenuOpen = false;
  protected alertsOpen = false;
  protected projects: ProjectOption[] = [];
  protected clients: ClientOption[] = [];
  protected selectedClientId = '';
  protected canChangeProject = false;
  protected loadingProject = false;
  projectName: string = '';
  clientName: string = '';
  private isSupportRoute = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly projectContext: ProjectContextService,
    protected readonly alerts: AlertsService,
    private readonly auth: AuthService,
    private readonly projectsStore: ProjectsStoreService,
    private readonly router: Router,
  ) {}

  async ngOnInit() {
    this.isSupportRoute = this.router.url.startsWith('/suporte');

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        const nextSupportRoute = this.router.url.startsWith('/suporte');
        if (nextSupportRoute !== this.isSupportRoute) {
          this.isSupportRoute = nextSupportRoute;
          this.refreshProjects();
        }
      });

    this.projectContext.projectId$.pipe(takeUntil(this.destroy$)).subscribe((projectId) => {
      if (projectId) {
        this.loadingProject = true;
      }
      if (this.showClientSelector) {
        this.syncSelectedFromProject(projectId);
        return;
      }

        if (this.auth.isClientAdmin || this.auth.isManager) {
        if (!this.projects.length && projectId) {
          void this.refreshProjects();
        }
        this.syncSelectedFromProject(projectId);
        return;
      }

      if (projectId) {
        this.loadProject(projectId);
      } else {
        this.projects = [];
        this.projectName = '';
        this.clientName = '';
        this.canChangeProject = false;
      }
    });

    this.projectsStore.projectUpdates$.pipe(takeUntil(this.destroy$)).subscribe((projectId) => {
      if (!projectId) return;
      if (this.showClientSelector || this.auth.isClientAdmin || this.auth.isManager) {
        void this.refreshProjects();
        return;
      }
      if (projectId === this.projectContext.projectId) {
        void this.loadProject(projectId, true);
      }
    });

    await this.refreshProjects();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected get projectId() {
    return this.projectContext.projectId;
  }

  protected get selectedProject() {
    return this.projects.find((p) => p.id === this.projectId) ?? null;
  }

  protected get selectedClient() {
    return (
      this.clients.find((c) => c.id === this.selectedClientId) ??
      this.clients.find((c) => c.projects.some((p) => p.id === this.projectId)) ??
      null
    );
  }

  protected get showClientSelector() {
    return this.auth.isAdminOrSupport && !this.isSupportRoute;
  }

  protected get showProjectSelector() {
    return !this.isSupportRoute;
  }

  protected get showSupportSwitcher() {
    return this.isSupportRoute;
  }

  protected get userName() {
    return this.auth.userName;
  }

  protected get projectLabel() {
    if (this.loadingProject) return 'Carregando projeto...';
    if (this.showClientSelector && this.selectedClient && this.selectedProject) {
      return `${this.selectedClient.name} / ${this.selectedProject.name}`;
    }
    return this.selectedProject?.name ?? 'Projeto não selecionado';
  }

  protected get projectIdDisplay() {
    return null;
  }

  protected toggleProjectMenu() {
    if (!this.canChangeProject) return;
    if (
      !this.projectMenuOpen &&
      (!this.projects.length || (this.showClientSelector && !this.clients.length))
    ) {
      void this.refreshProjects();
    }
    this.projectMenuOpen = !this.projectMenuOpen;
  }

  protected closeProjectMenu() {
    this.projectMenuOpen = false;
  }

  protected selectProject(projectId: string, clientId?: string) {
    this.loadingProject = true;
    this.projectContext.setProjectId(projectId);
    if (clientId) this.selectedClientId = clientId;
    this.projectMenuOpen = false;
    void this.refreshProjects();
  }

  protected toggleAlerts() {
    this.alertsOpen = !this.alertsOpen;
    this.projectMenuOpen = false;
  }

  protected closeAlerts() {
    this.alertsOpen = false;
  }

  protected get alertsList$() {
    return this.alerts.alerts$;
  }

  protected alertView(alert: AlertDto) {
    return this.alerts.getView(alert);
  }

  protected openAlert(alert: AlertDto) {
    this.alerts.openAlert(alert);
    this.alertsOpen = false;
  }

  protected async resolveAlert(alert: AlertDto, event?: Event) {
    event?.stopPropagation();
    this.alerts.openAlert(alert);
    this.alertsOpen = false;
  }

  protected async logout() {
    this.auth.logout();
    await this.router.navigateByUrl('/login');
  }

  private async refreshProjects() {
    const projectId = this.projectContext.projectId;
    if (this.showClientSelector) {
      await this.loadClients(projectId ?? undefined);
      return;
    }

    if (this.auth.isClientAdmin) {
      await this.loadProjectsByClient(this.auth.clientId, projectId ?? undefined);
      return;
    }

    if (this.auth.isManager) {
      await this.loadProjectsByIds(this.auth.projectIds, projectId ?? undefined);
      return;
    }

    const fallbackProjectId = projectId ?? this.auth.projectId;
    if (fallbackProjectId) {
      if (!this.projectContext.projectId) {
        this.projectContext.setProjectId(fallbackProjectId);
      }
      await this.loadProject(fallbackProjectId);
      return;
    }

    this.projects = [];
    this.clients = [];
    this.projectName = '';
    this.clientName = '';
    this.canChangeProject = false;
  }

  private async loadProject(projectId: string, forceRefresh = false) {
    const cached = forceRefresh ? null : this.projectsStore.getCachedProject(projectId);
    if (cached) {
      this.projectName = cached.name;
      this.projects = [{ id: cached._id, name: cached.name }];
      this.clients = [];
      this.clientName = '';
    }
    this.loadingProject = true;

    try {
      const project = await firstValueFrom(
        forceRefresh ? this.projectsStore.refreshProject(projectId) : this.projectsStore.getProject(projectId),
      );
      this.projectName = project.name;
      this.projects = [{ id: project._id, name: project.name }];
      this.clients = [];
      this.clientName = '';
      this.canChangeProject = this.auth.canChangeProject && this.projects.length > 1;
      if (!this.projectContext.projectId) this.projectContext.setProjectId(project._id);
    } catch {
      this.projects = [];
      this.clients = [];
      this.projectName = '';
      this.clientName = '';
      this.canChangeProject = false;
    } finally {
      this.loadingProject = false;
    }
  }

  private async loadProjectsByClient(clientId: string | null, preferredProjectId?: string) {
    this.loadingProject = true;
    try {
      if (!clientId) {
        this.projects = [];
        this.clients = [];
        this.projectName = '';
        this.clientName = '';
        this.canChangeProject = false;
        return;
      }

      const list = await firstValueFrom(this.projectsStore.listProjects({ client_id: clientId }));
      this.projects = (list ?? []).map((project) => ({
        id: project._id,
        name: project.name,
        clientId: project.client_id,
      }));
      this.clients = [];

      const currentId = preferredProjectId ?? this.projectContext.projectId;
      const hasCurrent = currentId ? this.projects.some((p) => p.id === currentId) : false;
      const nextProjectId = hasCurrent ? currentId : (this.projects[0]?.id ?? '');

      if (nextProjectId && nextProjectId !== this.projectContext.projectId) {
        this.projectContext.setProjectId(nextProjectId);
      }
      this.syncSelectedFromProject(nextProjectId);
      this.canChangeProject = this.auth.canChangeProject && this.projects.length > 1;
    } catch {
      this.projects = [];
      this.clients = [];
      this.projectName = '';
      this.clientName = '';
      this.canChangeProject = false;
    } finally {
      this.loadingProject = false;
    }
  }

  private async loadProjectsByIds(projectIds: string[], preferredProjectId?: string) {
    this.loadingProject = true;
    try {
      if (!projectIds.length) {
        this.projects = [];
        this.clients = [];
        this.projectName = '';
        this.clientName = '';
        this.canChangeProject = false;
        return;
      }

      const list = await firstValueFrom(this.projectsStore.listProjects({ ids: projectIds }));
      this.projects = (list ?? []).map((project) => ({
        id: project._id,
        name: project.name,
        clientId: project.client_id,
      }));
      this.clients = [];

      const currentId = preferredProjectId ?? this.projectContext.projectId;
      const hasCurrent = currentId ? this.projects.some((p) => p.id === currentId) : false;
      const nextProjectId = hasCurrent ? currentId : (this.projects[0]?.id ?? '');

      if (nextProjectId && nextProjectId !== this.projectContext.projectId) {
        this.projectContext.setProjectId(nextProjectId);
      }
      this.syncSelectedFromProject(nextProjectId);
      this.canChangeProject = this.auth.canChangeProject && this.projects.length > 1;
    } catch {
      this.projects = [];
      this.clients = [];
      this.projectName = '';
      this.clientName = '';
      this.canChangeProject = false;
    } finally {
      this.loadingProject = false;
    }
  }

  private async loadClients(preferredProjectId?: string) {
    this.loadingProject = true;
    try {
      const result = await firstValueFrom(this.projectsStore.listClients(true));
      const clients = (result ?? []).map((client) => ({
        id: client._id,
        name: client.name,
        projects: (client.projects ?? []).map((project) => ({
          id: project._id,
          name: project.name,
          clientId: client._id,
        })),
      }));
      this.clients = clients;
      this.projects = clients.flatMap((client) => client.projects);

      const currentId = preferredProjectId ?? this.projectContext.projectId;
      const hasCurrent = currentId ? this.projects.some((p) => p.id === currentId) : false;
      const nextProjectId = hasCurrent ? currentId : (this.projects[0]?.id ?? '');

      if (nextProjectId && nextProjectId !== this.projectContext.projectId) {
        this.projectContext.setProjectId(nextProjectId);
      }

      this.syncSelectedFromProject(nextProjectId);
      this.canChangeProject = this.auth.canChangeProject && this.projects.length > 1;
    } catch {
      this.projects = [];
      this.clients = [];
      this.projectName = '';
      this.clientName = '';
      this.canChangeProject = false;
    } finally {
      this.loadingProject = false;
    }
  }

  private syncSelectedFromProject(projectId: string | null) {
    if (!projectId) {
      this.projectName = '';
      this.clientName = '';
      this.selectedClientId = '';
      this.loadingProject = false;
      return;
    }

    const project = this.projects.find((p) => p.id === projectId) ?? null;
    const client =
      this.clients.find((c) => c.id === project?.clientId) ??
      this.clients.find((c) => c.projects.some((p) => p.id === projectId)) ??
      null;

    if (project) this.projectName = project.name;
    if (client) {
      this.selectedClientId = client.id;
      this.clientName = client.name;
    }
    this.loadingProject = false;
  }
}
